import React, { useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { GET_LICENSE_BY_GUIDE } from '../reports.graphql';
import { Box, Typography, Paper, IconButton, Link, Stack } from '@mui/material';
import LoadingSpinner from '../../components/LoadingSpinner';
import UseLogTable from './UseLogTable';
import XLSX from 'xlsx';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExcelLogo from '../../assets/microsoft-excel.svg?react';
import { DatePicker } from '@mui/x-date-pickers';
import moment from 'moment';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import { nbNO } from '@mui/x-data-grid/locales';

interface DownloadModel {
  count: number;
  type: string;
}
interface CustomerModel {
  customerId: number;
  name: string;
  count: number;
  email: string;
  phoneNumber: string;
  downloads: DownloadModel[];
}
interface CustomerTableProps {
  guideId: number;
  handleBack: () => void;
  selectedGuide: string;
  showInternalBackButton?: boolean;
}
export interface CustomerTableHandle {
  handleBackNavigation: () => boolean;
}
const CustomerTable = React.forwardRef<CustomerTableHandle, CustomerTableProps>(function CustomerTable(props, ref) {
  const { guideId, handleBack, selectedGuide, showInternalBackButton = true } = props;
  const [from, setFrom] = useState<moment.Moment | null>(moment().subtract(6, 'months'));
  const [to, setTo] = useState<moment.Moment | null>(null);
  let [customerId, setCustomerId] = useState(0);
  let [isCustomerVisible, setIsCustomerVisible] = useState(true);
  let [customerName, setCustomerName] = useState();
  let [email, setEmail] = useState();
  const handleCustomerClick = (id, name, email) => {
    setCustomerId(id);
    setCustomerName(name);
    setIsCustomerVisible(false);
    setEmail(email);
  };
  const backToCustomerList = () => {
    setIsCustomerVisible(true);
  };
  const handleClick = () => {
    handleBack();
  };
  React.useImperativeHandle(
    ref,
    () => ({
      handleBackNavigation: () => {
        if (!isCustomerVisible) {
          backToCustomerList();
          return true;
        }
        return false;
      },
    }),
    [isCustomerVisible],
  );
  const renderBreadCrumb = () => {
    return (
      <Stack spacing={2}>
        <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
          {showInternalBackButton && (
            <IconButton onClick={() => handleClick()} title="Gå tilbake til anvisningsliste">
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography variant="h5">{selectedGuide}</Typography>
        </Box>
        <Box
          sx={{
            padding: '16px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            marginBottom: '16px',
            marginLeft: '16px',
            marginRight: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}>
          <DatePicker disableFuture label="Fra dato" value={from} onChange={setFrom} />
          <DatePicker disableFuture label="Til dato" value={to} onChange={setTo} />
          {data && <CustomerTableExcelExport model={data.customers} />}
        </Box>
      </Stack>
    );
  };
  const renderTable = () => {
    const rows = (data?.customers ?? []).map((c: CustomerModel) => ({
      id: c.customerId,
      customerId: c.customerId,
      name: c.name,
      email: c.email,
      phoneNumber: c.phoneNumber,
      count: c.count,
      downloads: FormatDownloads(c.downloads),
    }));

    const columns: GridColDef<(typeof rows)[number]>[] = [
      {
        field: 'name',
        headerName: 'Kunde',
        minWidth: 160,
        flex: 1,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => (
          <Link component="button" onClick={() => handleCustomerClick(params.row.customerId, params.row.name, params.row.email)}>
            {params.row.name}
          </Link>
        ),
      },
      { field: 'email', headerName: 'Epost kontakt', minWidth: 160, flex: 1, align: 'left', headerAlign: 'left' },
      { field: 'phoneNumber', headerName: 'Telefon', minWidth: 160, flex: 1, align: 'left', headerAlign: 'left' },
      { field: 'count', headerName: 'Antall søk', minWidth: 160, flex: 1, align: 'left', headerAlign: 'left' },
      { field: 'downloads', headerName: 'Nedlastinger', minWidth: 160, flex: 1, align: 'left', headerAlign: 'left' },
    ];

    return (
      <Paper elevation={1} sx={{ p: 2 }}>
        {loading && <LoadingSpinner />}
        <DataGridPro
          autoHeight
          density="compact"
          rows={rows}
          columns={columns}
          localeText={nbNO.components.MuiDataGrid.defaultProps.localeText}
          pagination
          pageSizeOptions={[25, 50, 100]}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 50,
                page: 0,
              },
            },
          }}
          sx={{
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 700,
            },
          }}
        />
      </Paper>
    );
  };
  const { loading, data } = useQuery<{ customers: CustomerModel[] }>(GET_LICENSE_BY_GUIDE, {
    variables: { guideId: guideId, fromDate: from, toDate: to },
    fetchPolicy: 'network-only',
  });
  if (isCustomerVisible) {
    return (
      <Stack spacing={2}>
        {renderBreadCrumb()}
        {renderTable()}
      </Stack>
    );
  } else {
    return (
      <UseLogTable
        selectedGuide={selectedGuide}
        email={email}
        customerName={customerName}
        customerId={customerId}
        guideId={guideId}
        fromDate={from}
        toDate={to}
        callback={() => backToCustomerList()}
        showInternalBackButton={showInternalBackButton}
      />
    );
  }
});
export default CustomerTable;
const FormatDownloads = (downloads) => {
  let result = '';
  downloads.forEach((download) => {
    if (downloads[downloads.length - 1] === download) {
      result = result.concat(download.type, '(', download.count, ')');
    } else {
      result = result.concat(download.type, '(', download.count, ')', ',');
    }
  });
  return result;
};
function CustomerTableExcelExport({ model }: { model: CustomerModel[] }) {
  const exportFile = () => {
    const workbook = XLSX.utils.book_new();
    const heading: string[] = ['Kunde', 'Epost kontakt', 'Telefon', 'Antall søk', 'Nedlastinger'];
    const workBookName = 'Kunde antall søk';
    const data = model.map((x) => [x.name, x.email, x.phoneNumber, x.count, FormatDownloads(x.downloads)]);
    const input = [heading, ...data];
    const sheet = XLSX.utils.aoa_to_sheet(input);
    XLSX.utils.book_append_sheet(workbook, sheet, workBookName);
    XLSX.writeFile(workbook, 'kundesøk.xlsx');
  };
  return (
    <IconButton onClick={exportFile} title="Eksport til Excel" size="large">
      <ExcelLogo />
    </IconButton>
  );
}
